<template>
  <div 

    :class="{'group-card': true, unread: !group.is_read && !expanded}"
    >

    <!-- Group Header -->
    <div
      class="group-header"
      @click="handleGroupClick"
      tabindex="0"
      role="button"
      :aria-expanded="group.expanded"
      :data-group-id="group.id"
      :data-group="group.group"
    >
      <div class="expander" v-if="hasChildren">
        <i 
          :class="expanded ? 'icon-arrow-top' : 'icon-arrow-bottom'"
          aria-hidden="true"
        ></i>
      </div>

      <div class="avatar">
        <img loading="lazy" 
          v-if="(group as any).image_s" 
          :src="(group as any).image_s" 
          :alt="(group as any).name"
          class="avatar-img"
        />
        <i v-else class="icon-bell" aria-hidden="true"></i>
      </div>

      <div class="body">
        <div class="title">{{ (group as any).name }}  
          <span v-if="group.cnt_unread" class="count unread">+{{ group.cnt_unread }}</span>
          <!-- <span v-else-if="group.cnt_total" class="count read">{{group.cnt_total}}</span> -->
        </div>
        <!-- <div v-if="groupMessage" class="message">{{ groupMessage }}</div> -->
      </div>

      <div 
        class="remove-btn" 
        @click.stop.prevent="removeGroup"
        >
        <i class="icon-close" aria-label="Remove group"></i>
      </div>
    </div>

    <!-- Expanded Children -->
    <transition name="expand" appear>
      <div v-if="expanded" class="children-container">
        <div ref="cardRef" class="card-inside"></div>
        <div v-if="group.items_notification?.length" class="children">
          <NotificationCard
            v-for="(item, index) in group.items_notification"
            :key="`${item.group}-${index}`"
            :notification="item"
            :show-template="true"
            @remove="removeChild(item.id)"
            @read:notification="readNotification(item)"
            class="child-notification"
          />
        </div> 
        <div v-if="(group as any).cnt_total > childrenCount" class="load-more">
          <UiButton             
            type="outline small"
            @click="loadMoreChildren"
          >
            Load more
          </UiButton>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">

const { $api } = useNuxtApp()

import { computed, ref } from 'vue'
import type { NotificationGroup } from '~/types/notification'
import { NotificationChannel } from '~/types/notification'
import NotificationCard from './NotificationCard.vue'
import UiButton from '~/components/ui/Button.vue'
import { useNotifications, useNotificationViewed } from '@/composables/useNotifications'

const props = defineProps<{ 
  group: NotificationGroup
  channel: 'direct' | 'following' | 'community'
  groupIndex: number
}>()

const emit = defineEmits<{
  (e: 'toggle-expand'): void
  (e: 'remove-group', groupId: string, channel: string, cnt: number, period: string): void
  (e: 'remove-child', id: string, channel: string): void
  (e: 'load-more-children'): void
}>()

const handleGroupClick = () => {
  // if (!props.group.is_read) {
  //   emit('mark-read')
  // }
  // emit('toggle-expand')
  expanded.value = !expanded.value
}
const start = ref<number>(0)
const limit = ref<number>(20)
const isEnded = ref<boolean>(false)
const expanded = ref(false)
// const { cardRef, isRead } = useNotificationViewed(props.group.is_read, props.group)
const removeGroup = () => {  
  emit('remove-group', (props.group as any).group_id || props.group.id, props.channel, (props.group as any).cnt_total || 0, (props.group as any).group_period || 'earlier') 
}
const removeChild = (id: string) => {
  if ((props.group as any).cnt_total) (props.group as any).cnt_total -= 1
  emit('remove-child', id, props.channel)
}

const readNotification = (notification: NotificationItem) => {
  props.group.cnt_unread -= 1
  props.group.is_read = props.group.cnt_unread <= 0
}

const hasChildren = computed(() => {
  return props.group.items_notification && props.group.items_notification.length > 0
})

const childrenCount = computed(() => {
  return props.group.items_notification?.length || 1
})
 
  
const prepared = computed(() => {
  let item = {   
    img: (props.group.contexts.item_user as any).avatar_little,
    message: useNotifications().formatNotificationGroupText(props.group)
  }
  return item
})


const loadMoreChildren = async () => {
  let data = await loadData()
  props.group.items_notification = [...props.group.items_notification, ...data.items]
}



const loadData = async function() {
  return await $api.getNotificationsV2ByGroup(useAuth().getId(), (props.group as any).channel, (props.group as any).group, (props.group as any).items_notification.length, limit.value, (props.group as any).group_period)
}


</script>

<style scoped>
.group-card{
  border-radius:8px;
  transition:background-color 1000ms ease,filter 1000ms ease;
  overflow:hidden;
}
.group-card.unread{ background-color:var(--gd-background-color-green-2-transparent-light); }


.group-header{
  display:flex;
  align-items:center;
  gap:12px;
  padding:8px 12px;
  cursor:pointer;
}

.avatar{ flex:0 0 32px; width:32px; height:32px; display:flex; align-items:center; justify-content:center; border-radius:50%; overflow:hidden; }
.avatar-img{ width:100%; height:100%; object-fit:cover; }
.avatar i{ font-size:20px; color:var(--accent); }

.body{ flex:1 1 auto; overflow:hidden; }
.title{ font-size:14px; font-weight:600; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.message{ font-size:12px; color:var(--text-secondary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.meta{ flex-shrink:0; display:flex; align-items:center; gap:4px; position:relative; }
.time{ font-size:10px; color:var(--text-secondary); }
.count{ 
  background-color:var(--un-background-color-gray); 
  color: var(--un-text-color);
  font-size:10px; 
  font-weight:600; 
  padding:2px 6px; 
  border-radius:12px; 
  margin-left:6px;
}
.count.unread{ 
  background-color:var(--un-firm);  
  color:#fff; 
} 
.dot{ width:8px; height:8px; border-radius:50%; background-color:var(--accent); display:block; }
.expand-icon{ font-size:12px; color:var(--text-secondary); transition:transform .2s; }
.close-btn{ font-size:12px; cursor:pointer; opacity:0.7; transition:opacity .2s; }
.close-btn:hover{ opacity:1; }

.children-container{ border-top:1px solid var(--bg-hover); }
.children{ padding:4px 0; 
  display: flex;
    flex-direction: column;
    gap: 0.2rem;
}
.child-notification{ margin-left:44px; }

.load-more{ padding:8px 44px; }



.remove-btn{
  background-color: var(--un-background-color-gray-darker);
  width:30px;
  height:30px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  transition:background-color 200ms ease;
  line-height:1;
  opacity: 0.7;
  flex-shrink:0;
}
.remove-btn:hover{ background-color: var(--un-background-color-gray-dark); }

/* Expand animation */
.expand-enter-active,
.expand-leave-active{ transition:all 200ms ease; }
.expand-enter-from,
.expand-leave-to{ opacity:0; max-height:0; }
.expand-enter-to,
.expand-leave-from{ opacity:1; max-height:1000px; }

/* Анимация просмотра */
.group-card.viewed-animation {
  animation: viewedPulse 2s ease-in-out;
}

@keyframes viewedPulse {
  0% {
    background-color: transparent;
  }
  50% {
    background-color: var(--accent-light);
  }
  100% {
    background-color: transparent;
  }
}
</style> 
